/**
 * An agent's Steps tab: its steps and hand-offs as a graph (laid out by steps.ts), and a
 * side panel that edits whichever step or hand-off is selected. A step's "+" adds the next
 * step with a hand-off to it. Edits go to the editor's draft, saved with the header's Save.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { Controls, Handle, MarkerType, Position, type NodeProps, type ReactFlowInstance } from 'reactflow';
import 'reactflow/dist/style.css';
import { Lock, Maximize2, MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  CAPABILITIES,
  GATE,
  MODELS,
  PLIABLE,
  connectorOf,
  eventLabel,
  fromList,
  triggerLabel,
  triggerSource,
  type Capability,
  type Connection,
  type Edge,
  type Member,
  type TeamDoc,
} from './catalog';
import { Glyph, HandoffDialog, OutputDialog, ToolDialog, TriggerDialog, TriggerGlyph } from './StepDialogs';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  addStep,
  handoffIndex,
  removeStep,
  setStep,
  stepIndex,
  stepNodeId,
  stepsGraph,
  type StartsNodeData,
  type StepNodeData,
} from './steps';

type Selected = { kind: 'step' | 'handoff'; index: number } | null;

const MODEL_BADGE: Record<string, string> = {
  opus: 'bg-primary text-primary-foreground',
  sonnet: 'bg-primary/10 text-primary',
  haiku: 'bg-muted text-muted-foreground',
};

function StepNode({ data, selected }: NodeProps<StepNodeData & { onAdd: () => void }>) {
  return (
    <div
      style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}
      className={cn(
        'relative rounded-lg border bg-card text-card-foreground px-3 py-2.5 shadow-sm',
        selected && 'border-primary ring-[3px] ring-primary/20',
      )}
    >
      {/* Anchors for hand-off lines only: hand-offs are added with a step's "+", not drawn. */}
      <Handle id="in" type="target" position={Position.Top} className="opacity-0" />
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold truncate">{data.name}</span>
        <span className={cn('ml-auto rounded px-1.5 py-0.5 text-[11px] font-medium capitalize', MODEL_BADGE[data.model] ?? MODEL_BADGE.haiku)}>
          {data.model}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1">
        {data.connectors.map((c) => (
          <Glyph key={c.connector} connector={c.connector} count={c.count} />
        ))}
        {data.budget != null && <span className="ml-auto text-[11px] text-muted-foreground">${data.budget} / run</span>}
      </div>
      <Handle id="out" type="source" position={Position.Bottom} className="opacity-0" />
      <Handle id="back-out" type="source" position={Position.Right} style={{ top: '65%' }} className="opacity-0" />
      <Handle id="back-in" type="target" position={Position.Right} style={{ top: '35%' }} className="opacity-0" />
      <button
        type="button"
        aria-label={`Add a step after ${data.name}`}
        title="Add a step after this one"
        className="nodrag nopan absolute left-1/2 -bottom-3 -translate-x-1/2 h-6 w-6 rounded-full border bg-card text-muted-foreground shadow-sm flex items-center justify-center hover:text-foreground hover:border-primary"
        onClick={(e) => {
          e.stopPropagation();
          data.onAdd();
        }}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** What starts a step, as pills sitting just above it. */
function StartsNode({ data }: NodeProps<StartsNodeData>) {
  return (
    <div style={{ width: NODE_WIDTH, height: NODE_HEIGHT }} className="flex flex-wrap content-end justify-center gap-1.5">
      {data.triggers.map((t, i) => (
        <span key={i} className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border bg-card pl-1 pr-2.5 text-xs font-medium shadow-sm">
          <TriggerGlyph connector={t.connector} />
          <span className="truncate">{t.label}</span>
        </span>
      ))}
      <Handle id="out" type="source" position={Position.Bottom} className="opacity-0" />
    </div>
  );
}

const nodeTypes = { step: StepNode, starts: StartsNode };

export default function StepsGraph({ doc, setDoc, connections, hash }: {
  doc: TeamDoc;
  setDoc: (d: TeamDoc) => void;
  connections: Connection[];
  hash: string;
}) {
  const [selected, setSelected] = useState<Selected>(null);
  const [flow, setFlow] = useState<ReactFlowInstance | null>(null);
  const graph = useMemo(() => stepsGraph(doc, connections), [doc, connections]);
  const slack = connections.filter((c) => c.connector === 'slack');

  // A step added or removed can land outside the view; refit once React Flow has measured it.
  // Not on load, where fitView (or a step link) has already placed it.
  const stepCount = useRef(doc.members.length);
  useEffect(() => {
    if (stepCount.current === doc.members.length) return;
    stepCount.current = doc.members.length;
    const timer = setTimeout(() => flow?.fitView({ padding: 0.3, maxZoom: 1, duration: 200 }), 50);
    return () => clearTimeout(timer);
  }, [flow, doc.members.length]);

  // A step link from the tree (…/steps#step-<id>) selects and centres that step.
  useEffect(() => {
    const i = doc.members.findIndex((m) => m.id && `#step-${m.id}` === hash);
    const node = graph.nodes.find((n) => n.id === stepNodeId(i));
    if (!flow || !node) return;
    setSelected({ kind: 'step', index: i });
    flow.setCenter(node.position.x + NODE_WIDTH / 2, node.position.y + NODE_HEIGHT / 2, { zoom: 1, duration: 300 });
    // Only when the link or the loaded agent changes, not on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, flow, doc.id]);

  const add = (after?: string) => {
    setDoc(addStep(doc, after));
    setSelected({ kind: 'step', index: doc.members.length });
  };
  const nodes = graph.nodes.map((n) => ({
    ...n,
    data: n.type === 'step' ? { ...n.data, onAdd: () => add((n.data as StepNodeData).name) } : n.data,
    selected: selected?.kind === 'step' && n.id === stepNodeId(selected.index),
  }));
  const edges = graph.edges.map((e) => {
    if (e.data.starts) return { ...e, selectable: false, focusable: false, style: { stroke: 'hsl(var(--border))', strokeWidth: 1.5 } };
    const stroke = e.data.gated ? GATE : 'hsl(var(--muted-foreground))';
    // Selected, like a selected step: thicker, and its label ringed in the primary colour.
    const on = selected?.kind === 'handoff' && handoffIndex(e.id) === selected.index;
    return {
      ...e,
      selected: on,
      style: { stroke, strokeWidth: on ? 3 : 1.5, strokeDasharray: e.data.gated ? '5 4' : undefined },
      markerEnd: { type: MarkerType.ArrowClosed, color: stroke },
      labelStyle: { fill: e.data.gated ? GATE : 'hsl(var(--foreground))', fontSize: 11, fontWeight: e.data.gated ? 500 : 400 },
      labelBgStyle: { fill: 'hsl(var(--card))', stroke: on ? 'hsl(var(--primary))' : e.data.gated ? GATE : 'hsl(var(--border))', strokeWidth: on ? 2 : 1 },
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 4,
    };
  });

  const step = selected?.kind === 'step' ? doc.members[selected.index] : undefined;
  const handoff = selected?.kind === 'handoff' ? doc.edges[selected.index] : undefined;

  return (
    <div className="flex h-full min-h-[480px]">
      <div className="relative flex-1 min-w-0">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onInit={setFlow}
          // What starts a step is part of it: clicking its pills selects the step.
          onNodeClick={(_, n) => setSelected({ kind: 'step', index: n.type === 'starts' ? Number(n.id.slice('starts-'.length)) : stepIndex(n.id) })}
          onEdgeClick={(_, e) => e.id.startsWith('handoff-') && setSelected({ kind: 'handoff', index: handoffIndex(e.id) })}
          nodesDraggable={false}
          nodesConnectable={false}
          deleteKeyCode={null}
          fitView
          fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
          proOptions={{ hideAttribution: true }}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
        <Button variant="outline" size="icon" className="absolute top-3 left-3" aria-label="Add step" title="Add a step" onClick={() => add()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="w-[480px] flex-shrink-0 border-l overflow-auto bg-card">
        {step ? (
          <StepInspector
            // A modal opened on one step closes when another is selected.
            key={selected!.index}
            m={step}
            doc={doc}
            connections={connections}
            onSelect={setSelected}
            onChange={(m) => setDoc(setStep(doc, selected!.index, m))}
            onRemove={
              doc.members.length > 1
                ? () => {
                    setDoc(removeStep(doc, selected!.index)!);
                    setSelected(null);
                  }
                : undefined
            }
          />
        ) : handoff ? (
          <HandoffPanel
            key={selected!.index}
            e={handoff}
            doc={doc}
            slack={slack}
            onSelect={setSelected}
            onChange={(ne) => setDoc({ ...doc, edges: doc.edges.map((x, j) => (j === selected!.index ? ne : x)) })}
            onRemove={() => {
              setDoc({ ...doc, edges: doc.edges.filter((_, j) => j !== selected!.index) });
              setSelected(null);
            }}
          />
        ) : (
          <p className="p-6 text-sm text-muted-foreground">
            Click a step or a hand-off to edit it. To add a step that takes over from another, use the + under that step.
          </p>
        )}
      </div>
    </div>
  );
}

/** A small "label + control" stack. */
export function F({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-1 ${className ?? ''}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

/** A titled block of the step panel, with its add button on the right. */
function Section({ title, count, action, children }: { title: string; count?: number; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-t px-6 py-4">
      <div className="mb-2 flex items-center gap-1.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        {!!count && <span className="text-xs text-muted-foreground">{count}</span>}
        <div className="ml-auto">{action}</div>
      </div>
      {children}
    </section>
  );
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={onClick}>
      <Plus className="mr-1 h-3 w-3" /> {label}
    </Button>
  );
}

/** One trigger or tool, as a line; clicking it opens its editor in place. */
function Row({ glyph, onOpen, children, chips }: { glyph: React.ReactNode; onOpen: () => void; children: React.ReactNode; chips?: string[] }) {
  const shown = (chips ?? []).slice(0, 3);
  const more = (chips?.length ?? 0) - shown.length;
  return (
    <li>
      <button type="button" onClick={onOpen} className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent">
        {glyph}
        <span className="min-w-0 flex-1 truncate">{children}</span>
        {shown.map((c) => (
          <span key={c} className="max-w-[8rem] truncate rounded bg-muted px-1.5 text-xs text-muted-foreground">
            {c}
          </span>
        ))}
        {more > 0 && <span className="text-xs text-muted-foreground">+{more}</span>}
      </button>
    </li>
  );
}

/** A grant's params as short chips: "ENG", "ws-data-hub", "3 tools". */
const paramChips = (c: Capability) =>
  Object.entries(c.params).flatMap(([k, v]) => (k === 'tools' && Array.isArray(v) ? [`${v.length} tools`] : Array.isArray(v) ? v.map(String) : [String(v)]));

/** The selected step: a few properties, its instructions, then what starts it, its tools and its output (Linear's issue sidebar is the model). */
function StepInspector({ m, doc, connections, onSelect, onChange, onRemove }: {
  m: Member;
  doc: TeamDoc;
  connections: Connection[];
  onSelect: (s: Selected) => void;
  onChange: (m: Member) => void;
  onRemove?: () => void;
}) {
  // The modal open on this step: a tool or trigger by index (null: adding one), or the output fields.
  const [editing, setEditing] = useState<{ kind: 'tool' | 'trigger'; index: number | null } | { kind: 'output' } | null>(null);
  const [fullScreen, setFullScreen] = useState(false);
  const close = () => setEditing(null);
  const stepAt = (name: string) => doc.members.findIndex((x) => x.name === name);
  const from = doc.edges.filter((e) => e.to === m.name);
  const to = doc.edges.flatMap((e, i) => (e.from === m.name ? [{ e, i }] : []));
  const link = 'text-primary hover:underline';

  return (
    <div>
      <div className="flex items-center gap-2 px-6 pb-3 pt-5">
        <Input
          aria-label="Step name"
          value={m.name}
          onChange={(e) => onChange({ ...m, name: e.target.value })}
          className="-ml-2 h-9 flex-1 border-transparent bg-transparent px-2 font-heading text-lg md:text-lg font-semibold shadow-none hover:border-input"
        />
        {onRemove && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Step actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-destructive" onSelect={onRemove}>
                Remove step
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <dl className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-y-1 px-6 pb-4 text-[13px]">
        <dt className="text-muted-foreground">Model</dt>
        <dd>
          <Select value={m.model ?? 'sonnet'} onValueChange={(model) => onChange({ ...m, model })}>
            <SelectTrigger aria-label="Model" className="h-8 w-auto gap-2 border-transparent px-2 text-[13px] shadow-none hover:bg-accent">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODELS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </dd>
        <dt className="text-muted-foreground">Budget</dt>
        <dd className="flex items-center gap-1 px-2">
          <span className="text-muted-foreground">$</span>
          <Input
            type="number"
            min={0}
            step={0.5}
            aria-label="Budget per run in US dollars"
            className="h-8 w-16 border-transparent px-1 text-[13px] md:text-[13px] shadow-none hover:border-input"
            value={m.budget_usd ?? ''}
            onChange={(e) => onChange({ ...m, budget_usd: e.target.value === '' ? null : Number(e.target.value) })}
          />
          <span className="text-muted-foreground">per run</span>
        </dd>
        <dt className="text-muted-foreground">Starts</dt>
        <dd className="px-2 py-1">
          {from.length > 0 && (
            <>
              After{' '}
              {from.map((e, k) => (
                <span key={k}>
                  {k > 0 && ' or '}
                  <button type="button" className={link} onClick={() => onSelect({ kind: 'step', index: stepAt(e.from) })}>
                    {e.from}
                  </button>
                </span>
              ))}
              {m.triggers.length > 0 && ', or on its triggers'}
            </>
          )}
          {!from.length && (m.triggers.length ? 'On its triggers' : <span className="text-muted-foreground">Nothing yet: add a trigger, or a hand-off to it</span>)}
        </dd>
        {to.length > 0 && (
          <>
            <dt className="text-muted-foreground">Hands off to</dt>
            <dd className="flex flex-col items-start gap-1 px-2 py-1">
              {to.map(({ e, i }) => (
                <button key={i} type="button" className="inline-flex flex-wrap items-center gap-1.5 text-left" onClick={() => onSelect({ kind: 'handoff', index: i })}>
                  <span className={link}>{e.to}</span>
                  {e.gate && (
                    <span className="inline-flex items-center gap-1 rounded px-1.5 text-xs" style={{ color: GATE, background: `${GATE}14` }}>
                      <Lock className="h-3 w-3" aria-hidden /> approved first
                    </span>
                  )}
                </button>
              ))}
            </dd>
          </>
        )}
      </dl>

      <Section
        title="Instructions"
        action={
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={() => setFullScreen(true)}>
            <Maximize2 className="mr-1 h-3 w-3" /> Open full screen
          </Button>
        }
      >
        <Textarea
          aria-label="Instructions"
          rows={14}
          className="bg-muted/40 text-[13px] leading-5"
          value={m.prompt}
          placeholder="What this step does, step by step, and what it should never do."
          onChange={(e) => onChange({ ...m, prompt: e.target.value })}
        />
      </Section>
      <Dialog open={fullScreen} onOpenChange={setFullScreen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{m.name}: instructions</DialogTitle>
          </DialogHeader>
          <Textarea
            aria-label="Instructions"
            autoFocus
            className="min-h-[70vh] text-sm leading-6"
            value={m.prompt}
            onChange={(e) => onChange({ ...m, prompt: e.target.value })}
          />
        </DialogContent>
      </Dialog>

      <Section title="Triggers" count={m.triggers.length} action={<AddButton label="Add trigger" onClick={() => setEditing({ kind: 'trigger', index: null })} />}>
        {!m.triggers.length && <p className="text-xs text-muted-foreground">None: it starts only when another step hands off to it.</p>}
        <ul>
          {m.triggers.map((t, i) => {
            const source = triggerSource(t, connections);
            return (
              <Row key={i} glyph={<TriggerGlyph connector={source} />} onOpen={() => setEditing({ kind: 'trigger', index: i })}>
                {triggerLabel(t, eventLabel(source, t.event))}
              </Row>
            );
          })}
        </ul>
      </Section>

      <Section title="Tools" count={m.capabilities.length} action={<AddButton label="Add tool" onClick={() => setEditing({ kind: 'tool', index: null })} />}>
        <ul>
          {m.capabilities.map((c, i) => {
            const connector = connectorOf(c.connection, connections);
            const label = CAPABILITIES[connector]?.find((s) => s.capability === c.capability)?.label ?? c.capability;
            return (
              <Row key={i} glyph={<Glyph connector={connector} />} onOpen={() => setEditing({ kind: 'tool', index: i })} chips={paramChips(c)}>
                {label} <span className="text-muted-foreground">on {c.connection === PLIABLE ? 'Pliable' : c.connection || 'no connection'}</span>
              </Row>
            );
          })}
        </ul>
        <p className="mt-2 text-xs text-muted-foreground">Every step can also search the Knowledge Base and keep its own notes there.</p>
      </Section>

      <Section
        title="Output"
        action={
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={() => setEditing({ kind: 'output' })}>
            {Object.keys(m.output_fields).length ? 'Edit' : 'Add fields'}
          </Button>
        }
      >
        <p className="mb-2 text-xs text-muted-foreground">What the step fills in besides its summary.</p>
        <dl className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-y-2 text-[13px]">
          {Object.entries(m.output_fields).map(([k, allowed]) => (
            <div key={k} className="contents">
              <dt className="truncate font-medium">{k}</dt>
              <dd className="flex flex-wrap gap-1">
                {allowed.length ? (
                  allowed.map((a) => (
                    <span key={a} className="rounded bg-muted px-1.5 text-xs">
                      {a}
                    </span>
                  ))
                ) : (
                  <span className="text-muted-foreground">any text</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </Section>

      {editing?.kind === 'tool' && (
        <ToolDialog
          initial={editing.index === null ? null : m.capabilities[editing.index]!}
          granted={m.capabilities}
          connections={connections}
          onClose={close}
          onSubmit={(c) => {
            onChange({ ...m, capabilities: editing.index === null ? [...m.capabilities, c] : m.capabilities.map((x, j) => (j === editing.index ? c : x)) });
            close();
          }}
          onRemove={
            editing.index === null
              ? undefined
              : () => {
                  onChange({ ...m, capabilities: m.capabilities.filter((_, j) => j !== editing.index) });
                  close();
                }
          }
        />
      )}
      {editing?.kind === 'trigger' && (
        <TriggerDialog
          initial={editing.index === null ? null : m.triggers[editing.index]!}
          step={m.name}
          existing={m.triggers}
          connections={connections}
          onClose={close}
          onSubmit={(t) => {
            onChange({ ...m, triggers: editing.index === null ? [...m.triggers, t] : m.triggers.map((x, j) => (j === editing.index ? t : x)) });
            close();
          }}
          onRemove={
            editing.index === null
              ? undefined
              : () => {
                  onChange({ ...m, triggers: m.triggers.filter((_, j) => j !== editing.index) });
                  close();
                }
          }
        />
      )}
      {editing?.kind === 'output' && (
        <OutputDialog
          step={m}
          edges={doc.edges}
          onClose={close}
          onSubmit={(output_fields) => {
            onChange({ ...m, output_fields });
            close();
          }}
        />
      )}
    </div>
  );
}

/** The selected hand-off, read as a sentence; Edit opens it in a modal. */
function HandoffPanel({ e, doc, slack, onSelect, onChange, onRemove }: {
  e: Edge;
  doc: TeamDoc;
  slack: Connection[];
  onSelect: (s: Selected) => void;
  onChange: (e: Edge) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [field, value] = Object.entries(e.when)[0] ?? [];
  const stepLink = (name: string) => (
    <button type="button" className="text-primary hover:underline" onClick={() => onSelect({ kind: 'step', index: doc.members.findIndex((m) => m.name === name) })}>
      {name}
    </button>
  );
  const others = doc.edges.filter((x) => x !== e && x.from === e.from).length;
  return (
    <div>
      <div className="flex items-center gap-2 px-6 pb-3 pt-5">
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-lg font-semibold">Hand-off</h2>
          <p className="truncate text-[13px] text-muted-foreground">
            {e.from} to {e.to}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Hand-off actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem className="text-destructive" onSelect={onRemove}>
              Remove hand-off
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <dl className="grid grid-cols-[104px_minmax(0,1fr)] items-baseline gap-y-3.5 px-6 pb-5 text-[13px]">
        <dt className="text-muted-foreground">When</dt>
        <dd>
          {stepLink(e.from)} finishes
          {field ? (
            <>
              {' '}
              with <b className="font-semibold">{field}</b> set to <span className="rounded bg-muted px-1.5 text-xs">{fromList(value)}</span>
            </>
          ) : (
            ', with any output'
          )}
        </dd>
        <dt className="text-muted-foreground">First</dt>
        <dd>
          {e.gate ? (
            <>
              <span className="inline-flex items-center gap-1 font-medium" style={{ color: GATE }}>
                <Lock className="h-3 w-3" aria-hidden /> An Admin approves it
              </span>
              <div className="mt-0.5 text-muted-foreground">
                {e.gate.connection != null ? `In Pliable, or in Slack: channel ${e.gate.channel || '(not set)'} through ${e.gate.connection || '(no bot)'}` : 'In Pliable'}
              </div>
            </>
          ) : (
            <span className="text-muted-foreground">Nothing: it hands off straight away</span>
          )}
        </dd>
        <dt className="text-muted-foreground">Then</dt>
        <dd>{stepLink(e.to)} runs</dd>
      </dl>
      {!others && field && (
        <p className="border-t px-6 py-4 text-xs text-muted-foreground">When {e.from} finishes any other way, nothing runs after it.</p>
      )}
      {editing && (
        <HandoffDialog
          initial={e}
          steps={doc.members}
          slack={slack}
          onClose={() => setEditing(false)}
          onSubmit={(ne) => {
            onChange(ne);
            setEditing(false);
          }}
          onRemove={onRemove}
        />
      )}
    </div>
  );
}
